import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services';
import _ from 'lodash';
import type { Handler } from '../types/testVendorRequest.types';

interface UseHandlerOptionsParams {
  enabled?: boolean;
}

interface HandlerOption {
  id: number;
  name: string;
}

interface UseHandlerOptionsReturn {
  handlers: Handler[];
  isLoading: boolean;
  error: Error | null;
  comboboxOptions: HandlerOption[];
}

/**
 * Hook to fetch and format handler options for use in dropdowns.
 */
export const useHandlerOptions = ({ enabled = true }: UseHandlerOptionsParams = {}): UseHandlerOptionsReturn => {
  const { data: rawHandlers = [], isLoading, error } = useQuery<Handler[], Error>({
    queryKey: ['handlers'],
    queryFn: () => api.get('handlers').then((res) => res.data as Handler[]),
    enabled,
    select: (data) => _.orderBy(data, ['name'], ['asc']),
  });

  const comboboxOptions = useMemo<HandlerOption[]>(() => rawHandlers, [rawHandlers]);

  const selectOptions = useMemo(
    () =>
      rawHandlers.map((handler) => ({
        label: handler.name || `Handler ${handler.id}`,
        value: handler.id.toString(),
      })),
    [rawHandlers]
  );

  const handlerMap = useMemo(
    () => new Map<number, Handler>(rawHandlers.map((h) => [h.id, h])),
    [rawHandlers]
  );

  const getHandlerById = (id: number | string): Handler | undefined => handlerMap.get(Number(id));

  const getDisplayName = (handler: Handler | number | string | null): string => {
    if (!handler) return 'Not Selected';
    if (typeof handler === 'object' && handler.name) return handler.name;
    const found = getHandlerById(handler as number | string);
    return found?.name || `Handler #${handler}`;
  };

  return {
    handlers: rawHandlers,
    isLoading,
    error,
    comboboxOptions,
  };
};

export default useHandlerOptions;

